// @ts-strict-ignore
import { ProjectModel } from '@project/model/project.model';
import { Dictionary } from 'typescript-collections';
import { ProjectVO } from '@project/model/vo/project.vo';
import { CompositeModelDataHolder } from '@3d-scene/model3d/cache/model-data';
import { ArticleUsageVO, ArticleVO } from '../model/vo/article.vo';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { CompositeArticleUtils } from '../util/composite-article-utils';
import { ConnectionVO, MetaElementUsageIdentityVO } from '@shared/vo/meta-element.vo';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { CompositeArticleInformation } from '../model/composite-article-information';
import { Injectable } from '@angular/core';
import { ArticleUsageUtils } from '../util/article-usage-utils';
import { CompositeArticleInfoUtils } from '../util/composite-article-info-utils';

@Injectable({ providedIn: 'root' })
export class CompositeArticleManager {
  private static DELIMITER: string = '_';

  constructor(
    private projectModel: ProjectModel,
    private containerService: ContainerService,
    private compositeArticleUtils: CompositeArticleUtils,
    private compositeArticleInfoUtils: CompositeArticleInfoUtils,
  ) {}

  private _compositeArticleIdToVersion: Dictionary<string, Dictionary<string, Dictionary<string, Dictionary<string, string>[]>>> = new Dictionary<
    string,
    Dictionary<string, Dictionary<string, Dictionary<string, string>[]>>
  >();

  private isActive(projectType: string): boolean {
    // with SMAR-51 CompositeArticleManager deactivated for step 1
    // return !ProjectUtils.isMcsGdsProjectType(projectType);
    return false;
  }

  public addCompositeArticleProjects(compositeArticleProjectsMap: Dictionary<string, ProjectVO>, projectType: string): void {
    if (!this.isActive(projectType)) {
      return;
    }
    // key is presented as concatenation of articleId and articleVersion separated by the delimiter "_"

    for (const key of compositeArticleProjectsMap.keys()) {
      const compositeArticleIdAndVersion: string[] = key.split(CompositeArticleManager.DELIMITER);
      if (compositeArticleIdAndVersion.length !== 2) {
        continue;
      }

      const compositeArticleId: string = compositeArticleIdAndVersion[0];
      const compositeArticleVersion: string = compositeArticleIdAndVersion[1];
      this.internalAddCompositeArticleProject(compositeArticleId, compositeArticleVersion, compositeArticleProjectsMap.getValue(key));
    }
  }

  public parseCompositeModelHandler(compositeModelDataHolder: CompositeModelDataHolder): void {
    if (!this.isActive(this.projectModel.currentProjectType)) {
      return;
    }
    const compositeArticle: ArticleVO = compositeModelDataHolder.article;
    const compositeProject: ProjectVO = compositeModelDataHolder.complexProject.project;
    if (!compositeArticle || !compositeProject) {
      return;
    }

    this.internalAddCompositeArticleProject(compositeArticle.identity.toString(), compositeArticle.version.toString(), compositeProject);
  }

  public articleContainerAttachedHandler(data: any): void {
    if (!this.isActive(this.projectModel.currentProjectType)) {
      return;
    }
    const attachedContainer: ArticleContainer = data.container as ArticleContainer;
    const connectedTo: MetaElementUsageIdentityVO[] = data.connectedTo;
    if (this.compositeArticleInfoUtils.isCompositePartArticle(attachedContainer)) {
      return;
    }

    let compositeArticleUsageId: string = null;
    let compositeArticleNo: string = null;
    let compositePartUsageId: string = null;
    let compositeArticle: ArticleVO;
    for (const acceptingAreaId of connectedTo) {
      const acceptingContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(acceptingAreaId.articleUsageId);
      if (!this.compositeArticleInfoUtils.isCompositePartArticle(acceptingContainer)) {
        return;
      }

      const acceptingCompositeArticleUsageId: string = CompositeArticleInfoUtils.getCompositeArticleUsageId(acceptingContainer);
      if (compositeArticleUsageId && acceptingCompositeArticleUsageId !== compositeArticleUsageId) {
        return;
      }

      compositeArticleUsageId = acceptingCompositeArticleUsageId;
      compositeArticleNo = this.compositeArticleUtils.getCompositeArticleNo(acceptingContainer);
      compositeArticle = this.compositeArticleInfoUtils.getCompositeArticleFromContainer(acceptingContainer);
      if (!compositeArticle) {
        return;
      }

      const givingAreaId: MetaElementUsageIdentityVO = attachedContainer.findGivingArea(acceptingAreaId);
      if (!givingAreaId) {
        return;
      }

      const acceptingCompositePartUsageId: string = this.compositeArticleUtils.getCompositePartUsageId(acceptingContainer);
      if (!acceptingCompositePartUsageId) {
        return;
      }

      const acceptingKey: string = this.createAcceptingKey(acceptingContainer.articleUsage, acceptingAreaId, acceptingCompositePartUsageId);
      const givingKey: string = this.createGivingKey(attachedContainer.articleUsage, givingAreaId);

      const givingCompositePartUsageId: string = this.getCompositePartUsageId(
        acceptingKey,
        givingKey,
        compositeArticle.identity.toString(),
        compositeArticle.version.toString()
      );
      if (!givingCompositePartUsageId || (compositePartUsageId && givingCompositePartUsageId !== compositePartUsageId)) {
        return;
      }
      compositePartUsageId = givingCompositePartUsageId;
    }

    if (compositeArticleUsageId && compositeArticleNo && compositePartUsageId) {
      CompositeArticleUtils.applyCompositeArticleInformation(
        attachedContainer.articleUsage,
        new CompositeArticleInformation(
          compositeArticleUsageId,
          compositeArticle.identity,
          compositeArticle.version,
          compositeArticleNo,
          compositePartUsageId
        )
      );
    }
  }

  private getCompositePartUsageId(acceptingKey: string, givingKey: string, compositeArticleId: string, compositeArticleVersion: string): string {
    const versionDictionary: Dictionary<string, Dictionary<string, Dictionary<string, string>[]>> = this._compositeArticleIdToVersion.getValue(
      compositeArticleId
    );
    if (!versionDictionary) {
      return null;
    }

    const projectSchema: Dictionary<string, Dictionary<string, string>[]> = versionDictionary.getValue(compositeArticleVersion);
    if (!projectSchema) {
      return null;
    }

    const givings: Dictionary<string, string>[] = projectSchema.getValue(acceptingKey);
    if (!givings) {
      return null;
    }

    for (const giving of givings) {
      if (giving.getValue(givingKey)) {
        return giving.getValue(givingKey);
      }
    }

    return null;
  }

  private internalAddCompositeArticleProject(compositeArticleId: string, compositeArticleVersion: string, compositeArticleProject: ProjectVO): void {
    if (
      this._compositeArticleIdToVersion.containsKey(compositeArticleId) &&
      this._compositeArticleIdToVersion.getValue(compositeArticleId).containsKey(compositeArticleVersion)
    ) {
      return;
    }

    let versionDictionary: Dictionary<string, Dictionary<string, Dictionary<string, string>[]>> = this._compositeArticleIdToVersion.getValue(
      compositeArticleId
    );
    if (!versionDictionary) {
      versionDictionary = new Dictionary<string, Dictionary<string, Dictionary<string, string>[]>>();
      this._compositeArticleIdToVersion.setValue(compositeArticleId, versionDictionary);
    }
    versionDictionary.setValue(compositeArticleVersion, this.createCompositeArticleProjectSchema(compositeArticleProject));
  }

  private createCompositeArticleProjectSchema(project: ProjectVO): Dictionary<string, Dictionary<string, string>[]> {
    // The project schema represents a mapping of all connections in the composite article project
    // key - accepting data: articleId_articleVersion_metaElementName_compositeArticleUsageGUID
    // value - array of objects which map the key "articleId_articleVersion_metaElementName" to compositePartUsageId
    const projectSchema: Dictionary<string, Dictionary<string, string>[]> = new Dictionary<string, Dictionary<string, string>[]>();

    for (const composition of project.compositions) {
      for (const articleUsage of composition.articleUsages) {
        for (const connectionAreaUsage of ArticleUsageUtils.forwardConnectionAreaUsages(articleUsage)) {
            const connection: ConnectionVO = connectionAreaUsage.connection;
            const acceptingKey: string = this.createAcceptingKey(articleUsage, connection.acceptingAreaId, articleUsage.id);
            const givings: Dictionary<string, string>[] = [];
            for (const givingAreaId of connection.givingAreaIds) {
              const childArticleUsage: ArticleUsageVO = this.findArticleUsage(givingAreaId.articleUsageId, composition.articleUsages);
              const givingKey: string = this.createGivingKey(childArticleUsage, givingAreaId);
              const giving: Dictionary<string, string> = new Dictionary<string, string>();
              giving.setValue(givingKey, childArticleUsage.id);
              givings.push(giving);
            }
            projectSchema.setValue(acceptingKey, givings);
        }
      }
    }

    return projectSchema;
  }

  private findArticleUsage(articleUsageId: string, articleUsages: ArticleUsageVO[]): ArticleUsageVO {
    for (const articleUsage of articleUsages) {
      if (articleUsage.id === articleUsageId) {
        return articleUsage;
      }
    }

    return null;
  }

  private createAcceptingKey(articleUsage: ArticleUsageVO, acceptingAreaId: MetaElementUsageIdentityVO, compositePartUsageId: string): string {
    return (
      articleUsage.articleId +
      CompositeArticleManager.DELIMITER +
      articleUsage.articleVersion +
      CompositeArticleManager.DELIMITER +
      acceptingAreaId.metaElementName +
      CompositeArticleManager.DELIMITER +
      compositePartUsageId
    );
  }

  private createGivingKey(articleUsage: ArticleUsageVO, givingAreaId: MetaElementUsageIdentityVO): string {
    return (
      articleUsage.articleId +
      CompositeArticleManager.DELIMITER +
      articleUsage.articleVersion +
      CompositeArticleManager.DELIMITER +
      givingAreaId.metaElementName
    );
  }
}
